import React, { useState, useEffect, useCallback } from "react";
import {
  GoogleMap,
  useLoadScript,
  InfoWindow,
  Marker,
} from "@react-google-maps/api";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import axios from "axios";
import { useLocation, Link } from "react-router-dom";
import queryString from "query-string";
import "./DemoLocationFinder.css";
import Spinner from "./Spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faList,
  faArrowDown,
  faArrowUp,
  faTimes,
  faMapMarkerAlt,
  faBars,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import LeadGenForm from "./LeadGenForm";
import ReactPixel from "react-facebook-pixel";

const googleMapsLibraries = ["places"];

const AZNationalLocationFinder = () => {
  const [address, setAddress] = useState("");
  const [coordinates, setCoordinates] = useState({});
  const [radius, setRadius] = useState("");
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [sortCriteria, setSortCriteria] = useState({
    field: "price",
    ascending: true,
  });
  const [userLocation, setUserLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState({
    lat: 39.8283,
    lng: -98.5795,
  });
  const [mapZoom, setMapZoom] = useState(4);
  const [mapInstance, setMapInstance] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(true);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isLeadSubmitted, setIsLeadSubmitted] = useState(() =>
    localStorage.getItem("leadSubmitted") === "true"
  );
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const location = useLocation();
  const apiUrl = process.env.REACT_APP_API_URL;
  const ipInfoToken = "ad22ffe4eb7ee0";
  
  // Arizona National theme
  const theme = {
    primary: "#26314f", // AZ National blue
    secondary: "#c8872b", // AZ National gold
  };

  // Same hardcoded user ID for now
  const hardcodedUserId = "ks9oJULMW9WQagH04eLaQNOhvCo2";

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: googleMapsLibraries,
  });

  useEffect(() => {
    const params = queryString.parse(window.location.search);
    if (Object.keys(params).length > 0) {
      localStorage.setItem("marketingParams", JSON.stringify(params));
    }
  }, []);

  useEffect(() => {
    const pageName = location.pathname.replace("/", "") || "Home";
    ReactPixel.pageView({
      page: pageName,
      path: location.pathname,
      url: window.location.href,
    });
  }, [location]);

  useEffect(() => {
    const fetchUserLocation = async () => {
      try {
        const response = await axios.get(
          `https://ipinfo.io/json?token=${ipInfoToken}`
        );
        const [lat, lng] = response.data.loc.split(",");
        setUserLocation({ lat: parseFloat(lat), lng: parseFloat(lng) });
      } catch (error) {
        console.error("Error getting user location from IPInfo:", error);
      }
    };
    fetchUserLocation();
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    setCoordinates({});
  }, [address]);

  useEffect(() => {
    const isValid = address.trim() !== "" && radius > 0 && radius <= 3500;
    setIsFormValid(isValid);
  }, [address, radius]);

  const handleSelect = async (value) => {
    const results = await geocodeByAddress(value);
    const latLng = await getLatLng(results[0]);
    setAddress(value);
    setCoordinates(latLng);
  };

  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const toRad = (x) => (x * Math.PI) / 180;
    const R = 3958.8; // miles
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const fetchFuelStops = async (force = false) => {
    if (radius > 3500) {
      alert("Radius should be 3500 or below.");
      return;
    }
    if (!isAuthorized) {
      alert("Unauthorized access.");
      return;
    }

    // Lead capture disabled
    // if (!force && !isLeadSubmitted) {
    //   setIsLeadModalOpen(true);
    //   return;
    // }
    setLoading(true);

    let currentCoordinates = coordinates;
    const isValidCoordinates =
      coordinates &&
      typeof coordinates.lat === "number" &&
      typeof coordinates.lng === "number";

    if (!isValidCoordinates && address) {
      try {
        const results = await geocodeByAddress(address);
        const latLng = await getLatLng(results[0]);
        setCoordinates(latLng);
        currentCoordinates = latLng;
        setMapCenter(latLng);
        setMapZoom(10);
      } catch (error) {
        console.error("Error geocoding address:", error);
        setLoading(false);
        return;
      }
    }

    const data = {
      latitude: currentCoordinates.lat,
      longitude: currentCoordinates.lng,
      searchRange: radius,
      UserId: hardcodedUserId,
    };

    try {
      const response = await fetch(`${apiUrl}/getLocationsV2`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        console.error(
          "Fetch failed with status:",
          response.status,
          response.statusText
        );
        setLoading(false);
        return;
      }

      const locationsData = await response.json();
      if (userLocation) {
        locationsData.forEach((location) => {
          location.distance = calculateDistance(
            userLocation.lat,
            userLocation.lng,
            parseFloat(location.latitude),
            parseFloat(location.longitude)
          ).toFixed(2);
        });
      }
      setLocations(locationsData);
      setIsPanelOpen(true);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching fuel stops:", error);
      setLoading(false);
    }
  };

  const togglePanel = () => {
    setIsPanelOpen(!isPanelOpen);
  };

  useEffect(() => {
    if (mapInstance) {
      window.google.maps.event.trigger(mapInstance, "resize");
      mapInstance.setCenter(mapCenter);
    }
  }, [isPanelOpen, mapInstance, mapCenter]);

  const handleSort = (field) => {
    const isAscending =
      sortCriteria.field === field ? !sortCriteria.ascending : true;
    setSortCriteria({ field, ascending: isAscending });
  };

  const sortedLocations = [...locations].sort((a, b) => {
    let aValue, bValue;
    if (sortCriteria.field === "price") {
      aValue = parseFloat(a.userPrice) || Infinity;
      bValue = parseFloat(b.userPrice) || Infinity;
    } else if (sortCriteria.field === "distance") {
      aValue = parseFloat(a.distance) || Infinity;
      bValue = parseFloat(b.distance) || Infinity;
    }
    return sortCriteria.ascending ? aValue - bValue : bValue - aValue;
  });

  const handleMapLoad = useCallback((map) => {
    setMapInstance(map);
  }, []);

  const renderMarkers = () => {
    return sortedLocations.map((location) => {
      if (!location.retailPrice || location.retailPrice === "NA") return null;
      const markerIcon = {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
            <path fill="${theme.secondary}" d="M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z"/>
          </svg>
        `)}`,
        labelOrigin: new window.google.maps.Point(12, 45),
        scaledSize: new window.google.maps.Size(30, 30),
      };

      return (
        <Marker
          key={`marker-${location.locationID}`}
          position={{
            lat: parseFloat(location.latitude),
            lng: parseFloat(location.longitude),
          }}
          icon={markerIcon}
          onClick={() => setSelectedLocation(location)}
          label={{
            text:
              location.userPrice === "TBD"
                ? `$${parseFloat(location.retailPrice).toFixed(3)}`
                : `$${parseFloat(location.userPrice).toFixed(3)}`,
            color: "#ffffff",
            fontSize: "14px",
            fontWeight: "bold",
            className: "price-label",
          }}
        />
      );
    });
  };

  const renderInfoWindow = () => {
    if (!selectedLocation) return null;
    const savings = (
      parseFloat(selectedLocation.retailPrice) -
      parseFloat(selectedLocation.userPrice)
    ).toFixed(2);
    const price = isNaN(parseFloat(selectedLocation.userPrice))
      ? "$0.00"
      : `$${parseFloat(selectedLocation.userPrice).toFixed(3)}`;
    const retailPrice = isNaN(parseFloat(selectedLocation.retailPrice))
      ? "N/A"
      : `$${parseFloat(selectedLocation.retailPrice).toFixed(3)}`;
    const discount = isNaN(savings) ? "Pricing Unavailable" : `${savings} Off`;
    const distance = selectedLocation.distance ? `${selectedLocation.distance}` : "";
    const modifiedOn = new Date(selectedLocation.modifiedOn).toLocaleString();

    return (
      <InfoWindow
        position={{
          lat: parseFloat(selectedLocation.latitude),
          lng: parseFloat(selectedLocation.longitude),
        }}
        onCloseClick={() => setSelectedLocation(null)}
        options={{ pixelOffset: new window.google.maps.Size(0, -30) }}
      >
        <div className="info-window-content">
          <p className="price-note">
            *Prices may vary and are subject to change. We strive to provide
            accurate information, but discrepancies may occur.
          </p>
          <div className="info-window-details">
            <h2>{selectedLocation.locationName}</h2>
            <p className="directions-link">
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                  selectedLocation.addr2 +
                    ", " +
                    selectedLocation.city +
                    ", " +
                    selectedLocation.state +
                    " " +
                    selectedLocation.zip
                )}${
                  userLocation
                    ? `&origin=${userLocation.lat},${userLocation.lng}`
                    : ""
                }`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Directions
              </a>
            </p>
            <div className="price-info">
              <div className="user-price">{price}</div>
              <div className="price-label">Arizona National Price</div>
              <div className="retail-price">{retailPrice}</div>
              <div className="price-label">Retail Price</div>
              <div className="discount">{discount}</div>
            </div>
            <p className="address">
              {selectedLocation.addr1}, {selectedLocation.city},{" "}
              {selectedLocation.state} {selectedLocation.zip}
            </p>
            {distance && (
              <p className="distance">
                <FontAwesomeIcon
                  icon={faMapMarkerAlt}
                  className="mr-1"
                  style={{ color: theme.secondary }}
                />
                {distance} miles away
              </p>
            )}
            <p className="last-update">Last Price Update: {modifiedOn}</p>
          </div>
        </div>
      </InfoWindow>
    );
  };

  const handleLocationClick = (location) => {
    setSelectedLocation(location);
    const position = {
      lat: parseFloat(location.latitude),
      lng: parseFloat(location.longitude),
    };
    setMapCenter(position);
    setMapZoom(14);
    if (mapInstance) {
      mapInstance.panTo(position);
      mapInstance.setZoom(14);
    }
  };

  if (loadError) {
    return <div>Error loading maps</div>;
  }

  if (!isLoaded) {
    return <Spinner />;
  }

  return (
    <>
      {!isAuthorized ? (
        <div className="unauthorized">
          <h1>Unauthorized Access</h1>
          <p>You do not have permission to access this page.</p>
        </div>
      ) : (
        <div 
          className="location-finder" 
          style={{ backgroundColor: isMobile && locations.length > 0 ? "#f9fafc" : "#f9fafc" }}
        >
          {/* Header - Matching landing page */}
          <header className="bg-white shadow-lg border-b border-gray-100">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center py-4">
                {/* Logo */}
                <div className="flex items-center">
                  <img 
                    src="/logos/az_national_logo.png" 
                    alt="Arizona National Livestock Show" 
                    className="h-16 w-auto"
                  />
                </div>

                {/* Desktop Navigation */}
                <nav className="hidden lg:flex items-center space-x-8">
                  <div className="relative group">
                    <Link to="/" className="flex items-center text-[#26314f] hover:text-[#c8872b] font-medium transition-colors">
                      HOME
                    </Link>
                  </div>
                  <div className="relative group">
                    <button className="flex items-center text-[#26314f] hover:text-[#c8872b] font-medium transition-colors">
                      FUEL PROGRAM <FontAwesomeIcon icon={faChevronDown} className="ml-1 text-xs" />
                    </button>
                    <div className="absolute top-full left-0 mt-1 w-48 bg-white shadow-lg border rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                      <Link to="/aznationallocations" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#26314f]">
                        Find Locations
                      </Link>
                      <Link to="/registration" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#26314f]">
                        Sign Up
                      </Link>
                    </div>
                  </div>
                  <Link to="/#contact" className="text-[#26314f] hover:text-[#c8872b] font-medium transition-colors">
                    CONTACT
                  </Link>
                  <Link to="/registration" className="bg-[#c8872b] hover:bg-[#a06d1f] text-white px-6 py-2 rounded-full font-medium transition-colors">
                    SIGN UP
                  </Link>
                </nav>

                {/* Mobile menu button */}
                <div className="lg:hidden">
                  <button
                    onClick={() => setIsNavOpen(!isNavOpen)}
                    className="text-[#26314f] hover:text-[#c8872b] transition-colors"
                  >
                    <FontAwesomeIcon icon={faBars} className="text-xl" />
                  </button>
                </div>
              </div>

              {/* Mobile Navigation */}
              {isNavOpen && (
                <div className="lg:hidden border-t border-white/20 py-4">
                  <div className="space-y-3">
                    <Link to="/" className="block text-[#26314f] hover:text-[#c8872b] font-medium transition-colors">HOME</Link>
                    <Link to="/aznationallocations" className="block text-[#26314f] hover:text-[#c8872b] font-medium transition-colors">FUEL LOCATIONS</Link>
                    <Link to="/#contact" className="block text-[#26314f] hover:text-[#c8872b] font-medium transition-colors">CONTACT</Link>
                    <Link to="/registration" className="w-full text-left bg-[#c8872b] hover:bg-[#a06d1f] text-white px-4 py-2 rounded-full font-medium transition-colors inline-block">
                      SIGN UP
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </header>

          {/* Info Section */}
          <section
            className="info-section bg-gradient-to-b from-white to-gray-50"
            style={{ textAlign: "center", padding: "20px" }}
          >
            <h1
              className="text-[#c8872b]"
              style={{
                fontSize: "1.5rem",
                fontWeight: "bold",
                marginBottom: "1rem",
              }}
            >
              Arizona National Fuel Network
            </h1>
            <p style={{ marginBottom: "1rem", color: "#26314f" }}>
              Explore fuel savings at over 4,000+ locations nationwide while supporting 
              Arizona National's scholarship programs.
            </p>
            <p style={{ marginBottom: "1rem", color: "#26314f" }}>
              Save up to $0.15 per gallon on diesel purchases, with $0.05 going directly 
              to support youth scholarships and show premiums.
            </p>
            <p>
              <Link
                to="/registration"
                style={{ color: "#c8872b", textDecoration: "underline" }}
              >
                Join our program today
              </Link>              
            </p>
          </section>

          {/* Main Content */}
          <div 
            className={`main-content ${isMobile && locations.length > 0 ? 'pb-0' : ''}`} 
            style={{ 
              backgroundColor: isMobile && locations.length > 0 ? "transparent" : "#f8f9fa",
              paddingBottom: isMobile && locations.length > 0 ? "0" : undefined
            }}
          >
            {/* Search Form */}
            <div className={`search-form bg-white shadow-md p-4 rounded-lg mx-4 ${isMobile ? 'mb-0' : ''}`}>
              <div className="search-form-inner flex flex-wrap gap-4 justify-center">
                <div className="autocomplete flex-1 min-w-[200px]">
                  <PlacesAutocomplete
                    value={address}
                    onChange={setAddress}
                    onSelect={handleSelect}
                  >
                    {({
                      getInputProps,
                      suggestions,
                      getSuggestionItemProps,
                      loading: placesLoading,
                    }) => (
                      <div>
                        <input
                          {...getInputProps({
                            placeholder: "Search City/State",
                          })}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8872b] focus:border-[#c8872b] outline-none transition-colors"
                          required
                        />
                        <div className="autocomplete-dropdown">
                          {placesLoading && <div>Loading...</div>}
                          {suggestions.map((suggestion) => (
                            <div
                              {...getSuggestionItemProps(suggestion)}
                              key={suggestion.placeId}
                              className={`suggestion ${
                                suggestion.active ? "active" : ""
                              }`}
                            >
                              {suggestion.description}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </PlacesAutocomplete>
                </div>
                <input
                  type="number"
                  max="3500"
                  value={radius}
                  onChange={(e) => {
                    if (e.target.value <= 3500) {
                      setRadius(e.target.value);
                    }
                  }}
                  placeholder="Radius (miles)"
                  className="w-32 lg:w-32 w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8872b] focus:border-[#c8872b] outline-none transition-colors"
                  required
                />
                <button
                  onClick={() => fetchFuelStops()}
                  className="bg-[#c8872b] hover:bg-[#a06d1f] text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!isFormValid}
                >
                  Search
                </button>
                <button
                  onClick={togglePanel}
                  className="bg-[#26314f] hover:bg-[#c8872b] text-white font-semibold py-3 px-6 rounded-lg transition-colors hidden lg:flex items-center"
                >
                  <FontAwesomeIcon icon={faList} className="mr-2" />
                  {isPanelOpen ? "Hide List" : "Show List"}
                </button>
              </div>
            </div>

            {/* Loading Spinner */}
            {loading && <Spinner />}

            {/* Map and List */}
            {!isMobile && (
              <div className="content flex flex-col lg:flex-row gap-4 p-4">
              {/* Map - Hidden on mobile, styled on desktop */}
              <div
                className={`map-container hidden lg:flex flex-1 ${
                  isPanelOpen ? "lg:w-2/3" : "w-full"
                } rounded-lg overflow-hidden shadow-lg`}
                style={{ height: "100%" }}
              >
                <GoogleMap
                  onLoad={handleMapLoad}
                  center={mapCenter}
                  zoom={mapZoom}
                  mapContainerStyle={{ width: "100%", height: "100%" }}
                >
                  {renderMarkers()}
                  {renderInfoWindow()}
                </GoogleMap>
              </div>

              {/* Desktop List Panel */}
              {!isMobile && isPanelOpen && (
                <div className="list-panel w-full lg:w-1/3 bg-white shadow-md rounded-lg p-4 max-h-[100%] overflow-y-auto">
                  {locations.length > 0 && (
                    <div className="list-header flex justify-between items-center mb-4">
                      <div className="sort-options flex gap-2">
                        <span className="text-[#26314f]">Sort By:</span>
                        <button
                          onClick={() => handleSort("price")}
                          className="sort-button text-[#c8872b] hover:text-[#26314f]"
                        >
                          <FontAwesomeIcon
                            icon={
                              sortCriteria.field === "price" &&
                              sortCriteria.ascending
                                ? faArrowUp
                                : faArrowDown
                            }
                            className="mr-1"
                          />
                          Best Price
                        </button>
                        <button
                          onClick={() => handleSort("distance")}
                          className="sort-button text-[#c8872b] hover:text-[#26314f]"
                        >
                          <FontAwesomeIcon
                            icon={
                              sortCriteria.field === "distance" &&
                              sortCriteria.ascending
                                ? faArrowUp
                                : faArrowDown
                            }
                            className="mr-1"
                          />
                          Closest
                        </button>
                      </div>
                      <button
                        onClick={togglePanel}
                        className="btn btn-close text-[#26314f] hover:text-[#c8872b]"
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  )}
                  
                  {locations.length > 0 && (
                    <div className="price-note text-gray-600 text-sm mb-4">
                      <p>
                        *Prices may vary and are subject to change. We strive to
                        provide accurate information, but discrepancies may occur.
                      </p>
                    </div>
                  )}

                  {locations.length > 0 ? (
                    <div className="locations-list">
                      {sortedLocations.map((location) => {
                      const savings = (
                        parseFloat(location.retailPrice) -
                        parseFloat(location.userPrice)
                      ).toFixed(2);
                      const price = isNaN(parseFloat(location.userPrice))
                        ? "$0.00"
                        : `$${parseFloat(location.userPrice).toFixed(3)}`;
                      const retailPrice = isNaN(
                        parseFloat(location.retailPrice)
                      )
                        ? "N/A"
                        : `$${parseFloat(location.retailPrice).toFixed(3)}`;
                      const discount = isNaN(savings)
                        ? "Pricing Unavailable"
                        : `$${savings} Off`;
                      const distance = location.distance
                        ? `${location.distance}`
                        : "";
                      const modifiedOn = new Date(
                        location.modifiedOn
                      ).toLocaleString();

                      return (
                        <div
                          key={location.locationID}
                          className="location-item cursor-pointer p-4 border-b border-gray-200 hover:bg-gray-50"
                          onClick={() => handleLocationClick(location)}
                        >
                          <div className="location-item-inner flex justify-between">
                            <div className="location-info">
                              <div className="location-name font-bold text-[#26314f]">
                                {location.locationName}
                              </div>
                              <div className="directions-link">
                                <a
                                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                                    location.addr2 +
                                      ", " +
                                      location.city +
                                      ", " +
                                      location.state +
                                      " " +
                                      location.zip
                                  )}${
                                    userLocation
                                      ? `&origin=${userLocation.lat},${userLocation.lng}`
                                      : ""
                                  }`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[#c8872b] hover:underline"
                                >
                                  Directions
                                </a>
                              </div>
                              <div className="address text-gray-700">
                                {location.addr1}, {location.city},{" "}
                                {location.state} {location.zip}
                              </div>
                              {distance && (
                                <div className="distance text-gray-700">
                                  <FontAwesomeIcon
                                    icon={faMapMarkerAlt}
                                    className="mr-1"
                                    style={{ color: theme.secondary }}
                                  />
                                  {distance} miles away
                                </div>
                              )}
                            </div>
                            <div className="price-info text-right">
                              <div className="user-price text-[#c8872b] font-bold">
                                {price}
                              </div>
                              <div className="price-label text-gray-600 text-sm">
                                Arizona National Price
                              </div>
                              <div className="discount text-[#26314f] font-semibold">
                                {discount}
                              </div>
                              <div className="retail-price text-gray-700 line-through">
                                {retailPrice}
                              </div>
                              <div className="price-label text-gray-600 text-sm">
                                Retail Price
                              </div>
                            </div>
                          </div>
                          <div className="last-update text-gray-600 text-sm mt-2">
                            Last Price Update: {modifiedOn}
                          </div>
                        </div>
                      );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <FontAwesomeIcon 
                          icon={faMapMarkerAlt} 
                          className="text-[#26314f] text-5xl mb-4" 
                        />
                        <h3 className="text-xl font-semibold text-[#26314f] mb-2">
                          Find Fuel Locations
                        </h3>
                        <p className="text-gray-600 mb-4">
                          Enter a city or state above and search radius to discover fuel savings near you.
                        </p>
                        <p className="text-sm text-[#c8872b] font-medium">
                          Save up to $0.15/gallon on diesel while supporting Arizona National scholarship programs!
                        </p>
                      </div>
                    )
                  }
                </div>
              )}
              </div>
            )}

            {/* Mobile Cards - Inside main content */}
            {isMobile && locations.length > 0 && (
              <div className="pt-4 pb-4 px-4 max-h-96 overflow-y-auto">
                {sortedLocations.map((location) => {
                  const savings = (
                    parseFloat(location.retailPrice) -
                    parseFloat(location.userPrice)
                  ).toFixed(2);
                  const price = isNaN(parseFloat(location.userPrice))
                    ? "$0.00"
                    : `$${parseFloat(location.userPrice).toFixed(3)}`;
                  const discount = isNaN(savings)
                    ? "Pricing Unavailable"
                    : `$${savings} Off`;
                  const distance = location.distance
                    ? `${location.distance}`
                    : "";

                  return (
                    <div
                      key={location.locationID}
                      className="bg-white mb-3 rounded-xl shadow-sm border border-gray-100 overflow-hidden cursor-pointer"
                      onClick={() => handleLocationClick(location)}
                    >
                      <div className="p-4">
                        {/* Header with name and price */}
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1 pr-3">
                            <h3 className="font-bold text-[#26314f] text-lg leading-tight mb-1">
                              {location.locationName}
                            </h3>
                            <p className="text-gray-600 text-sm">
                              {location.city}, {location.state}
                            </p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="text-2xl font-bold text-[#c8872b]">
                              {price}
                            </div>
                            <div className="text-xs text-gray-500">AZ National</div>
                          </div>
                        </div>
                        
                        {/* Savings badge */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                            Save {discount}
                          </div>
                          {distance && (
                            <div className="flex items-center text-gray-600 text-sm">
                              <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-1 text-[#c8872b]" />
                              {distance} mi
                            </div>
                          )}
                        </div>
                        
                        {/* Address and directions */}
                        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                          <div className="text-sm text-gray-600 flex-1">
                            {location.addr1}
                          </div>
                          <a
                            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                              location.addr2 +
                                ", " +
                                location.city +
                                ", " +
                                location.state +
                                " " +
                                location.zip
                            )}${
                              userLocation
                                ? `&origin=${userLocation.lat},${userLocation.lng}`
                                : ""
                            }`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-[#26314f] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#c8872b] transition-colors ml-3"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Directions
                          </a>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Mobile Empty State */}
            {isMobile && locations.length === 0 && (
              <div className="text-center py-12 px-4 mt-4">
                <FontAwesomeIcon 
                  icon={faMapMarkerAlt} 
                  className="text-[#26314f] text-5xl mb-4" 
                />
                <h3 className="text-xl font-semibold text-[#26314f] mb-2">
                  Find Fuel Locations
                </h3>
                <p className="text-gray-600 mb-4">
                  Enter a city or state above and search radius to discover fuel savings near you.
                </p>
                <p className="text-sm text-[#c8872b] font-medium">
                  Save up to $0.15/gallon on diesel while supporting Arizona National scholarship programs!
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <footer className="footer1 bg-[#26314f] text-white py-6">
            <div className="container mx-auto px-6 text-center">
              <p>
                © {new Date().getFullYear()} Arizona National Livestock Show Fuel Program. All Rights
                Reserved.
              </p>
            </div>
          </footer>

          {/* Lead Gen Modal - DISABLED 
          {isLeadModalOpen && (
            <div className="lead-modal-overlay fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-2">
              <LeadGenForm
                onSuccess={() => {
                  localStorage.setItem("leadSubmitted", "true");
                  setIsLeadSubmitted(true);
                  setIsLeadModalOpen(false);
                  fetchFuelStops(true);
                }}
                onClose={() => setIsLeadModalOpen(false)}
              />
            </div>
          )}
          */}
        </div>
      )}
    </>
  );
};

export default AZNationalLocationFinder;